import { ref, computed, watch, type Ref } from 'vue';
import type { ItpDataGridColumn } from './types';
import {
  columnKey,
  autoFitWidth,
  createTextMeasurer,
  horizontalPaddingOf,
} from './gridCell';

export interface StoredColumn {
  field: string;
  width?: number;
  hidden?: boolean;
}

export interface StoredScroll {
  top: number;
  left: number;
}

export interface StoredColumnLayout {
  version: number;
  columns: StoredColumn[];
  scroll?: StoredScroll;
}

export const LAYOUT_VERSION = 1;

export const MIN_COLUMN_WIDTH = 60;

const WIDTH_KEYS = ['width', 'minWidth', 'maxWidth'] as const;

function pixels(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function normalizeColumnWidths(
  column: ItpDataGridColumn,
): ItpDataGridColumn {
  const needsParsing = WIDTH_KEYS.some(
    (key) => typeof column[key] === 'string',
  );
  if (!needsParsing) {
    return column;
  }
  const parsed = { ...column };
  for (const key of WIDTH_KEYS) {
    parsed[key] = pixels(column[key]);
  }
  return parsed;
}

// ── Persistence helpers (localStorage: order, width, scroll) ──
export function layoutStorageKey(
  name: string | undefined,
  stateKey: string | undefined,
): string | null {
  if (stateKey) {
    return stateKey;
  }
  return name ? `itp_grid_state_${name}` : null;
}

export function readLayout(key: string | null): StoredColumnLayout | null {
  if (!key) {
    return null;
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredColumnLayout;
    if (parsed?.version !== LAYOUT_VERSION || !Array.isArray(parsed.columns)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeLayout(
  key: string | null,
  layout: StoredColumnLayout,
): void {
  if (!key) {
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(layout));
  } catch (error) {
    console.warn('[ItpDataGrid] could not save the column layout', error);
  }
}

export function applyLayout(
  columns: ItpDataGridColumn[],
  layout: StoredColumnLayout | null,
): ItpDataGridColumn[] {
  if (!layout) {
    return columns;
  }

  const byKey = new Map(columns.map((column) => [columnKey(column), column]));
  const savedKeys = new Set(layout.columns.map((entry) => entry.field));

  const result: ItpDataGridColumn[] = [];
  for (const entry of layout.columns) {
    const column = byKey.get(entry.field);
    if (!column) {
      continue;
    }
    result.push({
      ...column,
      width: entry.width ?? column.width,
      hidden: entry.hidden ?? column.hidden,
    });
  }

  columns.forEach((column, index) => {
    if (savedKeys.has(columnKey(column))) {
      return;
    }
    const previous = index > 0 ? columns[index - 1] : null;
    const at = previous
      ? result.findIndex((item) => columnKey(item) === columnKey(previous)) + 1
      : 0;
    result.splice(at > 0 ? at : index, 0, column);
  });

  return result;
}

export function toStoredLayout(
  columns: ItpDataGridColumn[],
): StoredColumnLayout {
  return {
    version: LAYOUT_VERSION,
    columns: columns.map((column) => ({
      field: columnKey(column),
      width: column.width,
      hidden: column.hidden,
    })),
  };
}

// ── The composable: layer saved order / width / scroll over the columns defined in the page ──
export function useGridColumnLayout(
  columns: Ref<ItpDataGridColumn[]>,
  name: Ref<string | undefined>,
  stateKey: Ref<string | undefined>,
) {
  const storageKey = computed(() =>
    layoutStorageKey(name.value, stateKey.value),
  );

  const initialLayout = readLayout(storageKey.value);
  const overrides = ref<StoredColumnLayout | null>(initialLayout);
  const initialScroll = initialLayout?.scroll ?? null;
  let currentScroll: StoredScroll | undefined = initialLayout?.scroll;

  watch(storageKey, (key) => {
    const layout = readLayout(key);
    overrides.value = layout;
    currentScroll = layout?.scroll;
  });

  const layoutColumns = computed(() =>
    applyLayout(columns.value, overrides.value),
  );

  function persist(next: ItpDataGridColumn[]): void {
    const layout = toStoredLayout(next);
    overrides.value = layout;
    writeLayout(storageKey.value, { ...layout, scroll: currentScroll });
  }

  function saveScroll(scroll: StoredScroll): void {
    currentScroll = scroll;
    const base = overrides.value ?? toStoredLayout(layoutColumns.value);
    writeLayout(storageKey.value, { ...base, scroll });
  }

  function setWidths(entries: readonly { key: string; width: number }[]): void {
    if (!entries.length) {
      return;
    }
    const byKey = new Map(entries.map((entry) => [entry.key, entry.width]));
    persist(
      layoutColumns.value.map((column) => {
        const width = byKey.get(columnKey(column));
        return width == null
          ? column
          : { ...column, width: Math.max(MIN_COLUMN_WIDTH, Math.round(width)) };
      }),
    );
  }

  function setWidth(key: string, width: number): void {
    persist(
      layoutColumns.value.map((column) =>
        columnKey(column) === key
          ? { ...column, width: Math.max(MIN_COLUMN_WIDTH, Math.round(width)) }
          : column,
      ),
    );
  }

  function setHidden(key: string, hidden: boolean): void {
    persist(
      layoutColumns.value.map((column) =>
        columnKey(column) === key ? { ...column, hidden } : column,
      ),
    );
  }

  const hasHiddenColumns = computed(() => {
    const authored = new Map(
      columns.value.map((column) => [
        columnKey(column),
        column.hidden === true,
      ]),
    );
    return layoutColumns.value.some(
      (column) =>
        column.hidden === true && authored.get(columnKey(column)) !== true,
    );
  });

  function clearHidden(): void {
    persist(
      layoutColumns.value.map((column) => ({ ...column, hidden: undefined })),
    );
  }

  function moveColumn(fromKey: string, toKey: string): void {
    if (fromKey === toKey) {
      return;
    }
    const next = [...layoutColumns.value];
    const from = next.findIndex((column) => columnKey(column) === fromKey);
    const to = next.findIndex((column) => columnKey(column) === toKey);
    if (from === -1 || to === -1) {
      return;
    }
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persist(next);
  }

  function resetLayout(): void {
    overrides.value = null;
    currentScroll = undefined;
    if (storageKey.value) {
      try {
        localStorage.removeItem(storageKey.value);
      } catch (msg) {
        console.error(msg);
      }
    }
  }

  const isPersistent = computed(() => storageKey.value !== null);
  const hasStoredLayout = computed(() => overrides.value !== null);

  return {
    layoutColumns,
    setWidth,
    setWidths,
    setHidden,
    hasHiddenColumns,
    clearHidden,
    moveColumn,
    resetLayout,
    saveScroll,
    initialScroll,
    isPersistent,
    hasStoredLayout,
  };
}

// ── Track sizing: how wide each column gets, and how wide the rows may run ──
export const CHECKBOX_COLUMN_WIDTH = 36;
export const ROW_NUMBER_COLUMN_WIDTH = 68;
export const AUTO_COLUMN_MIN = 80;
export const AUTO_COLUMN_MAX = 200;

export function useGridTracks(
  visibleColumns: Ref<ItpDataGridColumn[]>,
  hasSystemColumn: Ref<boolean>,
  rowHeader: Ref<boolean>,
) {
  const systemColumnWidth = computed(() =>
    rowHeader.value ? ROW_NUMBER_COLUMN_WIDTH : CHECKBOX_COLUMN_WIDTH,
  );

  const gridTemplateColumns = computed(() => {
    const tracks = visibleColumns.value.map((column) =>
      column.width
        ? `${column.width}px`
        : `minmax(${AUTO_COLUMN_MIN}px, ${AUTO_COLUMN_MAX}px)`,
    );
    if (hasSystemColumn.value) {
      tracks.unshift(`${systemColumnWidth.value}px`);
    }
    tracks.push('1fr');
    return tracks.join(' ');
  });

  function totalWidth(fallbackPerColumn: number): number {
    let total = hasSystemColumn.value ? systemColumnWidth.value : 0;
    for (const column of visibleColumns.value) {
      total += column.width || fallbackPerColumn;
    }
    return total;
  }

  const frameStyle = computed(() => ({
    '--itp-grid-content-width': `${totalWidth(AUTO_COLUMN_MIN)}px`,
    '--itp-grid-max-content-width': `${totalWidth(AUTO_COLUMN_MAX)}px`,
  }));

  return { systemColumnWidth, gridTemplateColumns, frameStyle };
}

// ── Auto-fit ──
const SORT_INDICATOR_WIDTH = 22;

export const DEFAULT_MAX_AUTO_FIT_WIDTH = 600;

export interface AutoFitRequest {
  column: ItpDataGridColumn;
  position: number;
  hasSystemColumn: boolean;
  root: HTMLElement | null;
  rows: readonly any[];
  cellText: (row: any, column: ItpDataGridColumn) => string;
  hasComponent: boolean;
}

export function autoFitColumnWidth(request: AutoFitRequest): number | null {
  const { column, position, root, rows, cellText } = request;
  if (position === -1) {
    return null;
  }

  const sampleCell = root?.querySelector('.itp-data-grid__cell') ?? null;

  let renderedWidth = 0;
  if (request.hasComponent && root) {
    const index = position + (request.hasSystemColumn ? 2 : 1);
    for (const cell of root.querySelectorAll(
      `.itp-data-grid__row:not(.itp-data-grid__grouprow) .itp-data-grid__cell:nth-child(${index})`,
    )) {
      renderedWidth = Math.max(
        renderedWidth,
        (cell as HTMLElement).scrollWidth,
      );
    }
  }

  return autoFitWidth({
    headerText: column.name || column.field,
    texts: (function* () {
      for (const row of rows) {
        yield cellText(row, column);
      }
    })(),
    measure: createTextMeasurer(sampleCell),
    padding: horizontalPaddingOf(sampleCell) + SORT_INDICATOR_WIDTH,
    min: column.minWidth ?? MIN_COLUMN_WIDTH,
    max: column.maxWidth ?? DEFAULT_MAX_AUTO_FIT_WIDTH,
    renderedWidth,
  });
}
